/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'

import {
  Alert,
  Autocomplete,
  Box,
  Collapse,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTime } from 'luxon'

import USPSLocationFinder from '../USPSLocationFinder/USPSLocationFinder.component'

import { getCountriesAndCities } from '../../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import { CityAndAirports } from '../../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.types'
import { UspsLocation } from '../../../redux/states/usps/getUspsLocations/getUspsLocations.types'
import { RootState } from '../../../redux/store/store.types'
import { useAppDispatch, useAppSelector } from '../../../shared/hooks/redux.hooks'

export type DeliveryMethod = 'carrier_pickup' | 'drop_off' | null

export interface USPSDeliveryDetails {
  deliveryMethod: DeliveryMethod
  stayZipCode?: string
  // Structured pickup address fields
  pickupStreetAddress?: string
  pickupCity?: string
  pickupState?: string
  pickupZipCode?: string
  pickupDate?: Date
  dropoffLocationId?: string
  dropoffLocationData?: UspsLocation
}

interface USPSDeliveryMethodSelectorProps {
  /** Initial values (for editing existing data) */
  initialValues?: Partial<USPSDeliveryDetails>

  /** Callback when delivery details change */
  onChange: (details: USPSDeliveryDetails | null) => void

  /** Whether the fields are required */
  required?: boolean

  /** Show as optional with skip option */
  optional?: boolean

  /** Custom title */
  title?: string

  /** Custom description */
  description?: string
}

export default function USPSDeliveryMethodSelector({
  initialValues,
  onChange,
  required = false,
  optional = false,
  title = 'USPS Delivery',
  description = "Choose how you'll deliver the package to the recipient"
}: USPSDeliveryMethodSelectorProps) {
  const dispatch = useAppDispatch()

  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>(initialValues?.deliveryMethod || null)
  const [stayZipCode, setStayZipCode] = useState(initialValues?.stayZipCode || '')

  // Structured pickup address fields
  const [pickupStreetAddress, setPickupStreetAddress] = useState(initialValues?.pickupStreetAddress || '')
  const [pickupCity, setPickupCity] = useState(initialValues?.pickupCity || '')
  const [pickupState, setPickupState] = useState(initialValues?.pickupState || '')
  const [pickupZipCode, setPickupZipCode] = useState(initialValues?.pickupZipCode || '')

  const [pickupDate, setPickupDate] = useState<Date | null>(initialValues?.pickupDate || null)
  const [selectedLocation, setSelectedLocation] = useState<{
    locationID: string
    locationData: UspsLocation
  } | null>(
    initialValues?.dropoffLocationId && initialValues?.dropoffLocationData
      ? {
          locationID: initialValues.dropoffLocationId,
          locationData: initialValues.dropoffLocationData
        }
      : null
  )
  const [showFields, setShowFields] = useState(!optional || !!initialValues?.deliveryMethod)

  // Fetch countries and cities data
  const countriesAndCitiesData = useAppSelector((state: RootState) => state.getCountriesAndCities.success?.data)

  useEffect(() => {
    if (!countriesAndCitiesData) {
      dispatch(getCountriesAndCities({}))
    }
  }, [dispatch, countriesAndCitiesData])

  // Get US country data
  const usCountry = countriesAndCitiesData?.find((country) => country.iataCountryCode === 'US')

  // Get all US cities (these are the cities available in the dropdown)
  const usCities: CityAndAirports[] = usCountry?.cities || []

  // Validate ZIP codes
  const isValidStayZip = /^\d{5}$/.test(stayZipCode)
  const isValidPickupZip = /^\d{5}$/.test(pickupZipCode)

  // Notify parent of changes
  useEffect(() => {
    if (!deliveryMethod) {
      onChange(null)

      return
    }

    const details: USPSDeliveryDetails = {
      deliveryMethod,
      ...(deliveryMethod === 'drop_off' && { stayZipCode }),
      ...(deliveryMethod === 'carrier_pickup' && {
        pickupStreetAddress,
        pickupCity,
        pickupState,
        pickupZipCode
      }),
      ...(deliveryMethod === 'carrier_pickup' && pickupDate && { pickupDate }),
      ...(deliveryMethod === 'drop_off' &&
        selectedLocation && {
          dropoffLocationId: selectedLocation.locationID,
          dropoffLocationData: selectedLocation.locationData
        })
    }

    // Validate completeness
    const isComplete =
      deliveryMethod &&
      (deliveryMethod === 'carrier_pickup'
        ? pickupStreetAddress && pickupCity && pickupState && isValidPickupZip && pickupDate
        : isValidStayZip && selectedLocation)

    onChange(isComplete ? details : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryMethod,
    stayZipCode,
    pickupStreetAddress,
    pickupCity,
    pickupState,
    pickupZipCode,
    pickupDate,
    selectedLocation,
    isValidStayZip,
    isValidPickupZip
    // Note: onChange is intentionally excluded to prevent infinite loops
  ])

  if (optional && !showFields) {
    return (
      <Box sx={{ my: 2 }}>
        <Alert
          severity="info"
          action={
            <Typography
              component="span"
              sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 2 }}
              onClick={() => setShowFields(true)}
            >
              Set up now
            </Typography>
          }
        >
          Set up USPS delivery for this order
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      {/* Delivery Method Selection - FIRST */}
      <Typography variant="subtitle1" gutterBottom>
        Delivery Method
      </Typography>
      <RadioGroup value={deliveryMethod || ''} onChange={(e) => setDeliveryMethod(e.target.value as DeliveryMethod)}>
        <FormControlLabel
          value="drop_off"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="body1">Drop-off at USPS Location</Typography>
              <Typography variant="caption" color="text.secondary">
                You'll drop the package at a nearby USPS post office
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          value="carrier_pickup"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="body1">Carrier Pickup</Typography>
              <Typography variant="caption" color="text.secondary">
                USPS will pick up the package from your location
              </Typography>
            </Box>
          }
        />
      </RadioGroup>

      {/* Drop-off Flow: ZIP Code + Location Finder */}
      {deliveryMethod === 'drop_off' && (
        <Collapse in={deliveryMethod === 'drop_off'}>
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Your ZIP Code in USA"
              placeholder="e.g., 90001"
              value={stayZipCode}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5)
                setStayZipCode(value)
              }}
              required={required}
              helperText="Enter your ZIP code to find nearby USPS locations"
              sx={{ mb: 3 }}
              inputProps={{
                maxLength: 5,
                pattern: '[0-9]*'
              }}
            />

            {isValidStayZip && (
              <USPSLocationFinder
                recipientZipCode={stayZipCode}
                onLocationSelect={(locationID, locationData) => {
                  setSelectedLocation({ locationID, locationData })
                }}
                selectedLocationID={selectedLocation?.locationID}
              />
            )}

            {stayZipCode && !isValidStayZip && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please enter a valid 5-digit ZIP code
              </Alert>
            )}
          </Box>
        </Collapse>
      )}

      {/* Carrier Pickup Flow: Structured Address + Date */}
      {deliveryMethod === 'carrier_pickup' && (
        <Collapse in={deliveryMethod === 'carrier_pickup'}>
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              {/* Street Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  placeholder="Enter the street address where USPS should pick up the package"
                  value={pickupStreetAddress}
                  onChange={(e) => setPickupStreetAddress(e.target.value)}
                  required={required}
                  helperText="Include apartment, suite, or unit number if applicable"
                />
              </Grid>

              {/* Country (preselected to USA, disabled) */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={[{ value: 'US', label: 'United States' }]}
                  getOptionLabel={(option) => option.label}
                  value={{ value: 'US', label: 'United States' }}
                  disabled={true}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      helperText="USPS pickup is only available in the United States"
                    />
                  )}
                  fullWidth
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={usCities}
                  getOptionLabel={(option: CityAndAirports) => option.cityName}
                  value={usCities.find((city) => city.iataCityCode === pickupCity) || null}
                  onChange={(_, newValue: CityAndAirports | null) => {
                    setPickupCity(newValue ? newValue.iataCityCode : '')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      placeholder="Select city"
                      required={required}
                      helperText="Select your city"
                    />
                  )}
                  disabled={usCities.length === 0}
                  fullWidth
                />
              </Grid>

              {/* State/Province */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/Province"
                  placeholder="e.g., California"
                  value={pickupState}
                  onChange={(e) => setPickupState(e.target.value)}
                  required={required}
                  helperText="Enter your state or province"
                />
              </Grid>

              {/* ZIP Code */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  placeholder="e.g., 90001"
                  value={pickupZipCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5)
                    setPickupZipCode(value)
                  }}
                  required={required}
                  helperText="5-digit ZIP code"
                  inputProps={{
                    maxLength: 5,
                    pattern: '[0-9]*'
                  }}
                  error={pickupZipCode.length > 0 && !isValidPickupZip}
                />
              </Grid>
            </Grid>

            {pickupZipCode && !isValidPickupZip && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please enter a valid 5-digit ZIP code
              </Alert>
            )}

            <DatePicker
              label="Pickup Date"
              value={pickupDate ? DateTime.fromJSDate(pickupDate) : null}
              onChange={(date: DateTime | null) => setPickupDate(date ? date.toJSDate() : null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: required,
                  helperText: 'Select the date for USPS to pick up the package'
                }
              }}
              minDate={DateTime.now()}
            />
          </Box>
        </Collapse>
      )}

      {/* Optional skip button */}
      {optional && showFields && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              setShowFields(false)
              setDeliveryMethod(null)
              setStayZipCode('')
              setPickupStreetAddress('')
              setPickupCity('')
              setPickupState('')
              setPickupZipCode('')
              setPickupDate(null)
              setSelectedLocation(null)
            }}
          >
            Skip for now
          </Typography>
        </Box>
      )}
    </Box>
  )
}
